import React, { useEffect, useState } from 'react';
import { Box, Stack, TextField, Typography, Autocomplete, MenuItem } from '@mui/material';
import { fetchQuestionsByProfession, QuestionDto } from '../../api/questionsApi';
import { AnswerType, QuestionFilter } from '../../models/worker';

interface Props {
  language?: string;
  professionKeys: string[];
  value: QuestionFilter[];
  onChange: (filters: QuestionFilter[]) => void;
}

const DynamicProfessionFilters: React.FC<Props> = ({ language, professionKeys, onChange }) => {
  const [questions, setQuestions] = useState<QuestionDto[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!professionKeys || professionKeys.length === 0) {
      setQuestions([]);
      onChange([]);
      return;
    }
    fetchQuestionsByProfession(professionKeys, language)
      .then(setQuestions)
      .catch(() => setQuestions([]));
  }, [professionKeys.join(','), language]);

  useEffect(() => {
    const filters: QuestionFilter[] = [];
    for (const q of questions) {
      const v = answers[q.questionKey];
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) continue;
      switch (q.answerType as AnswerType) {
        case 'BOOLEAN':
          if (v !== 'any') filters.push({ key: q.questionKey, answerType: 'BOOLEAN', operator: 'equals', value: v === 'yes' });
          break;
        case 'DATE':
          if (v && (v.from || v.to)) filters.push({ key: q.questionKey, answerType: 'DATE', operator: 'between', value: { from: v.from || undefined, to: v.to || undefined } });
          break;
        case 'NUMBER':
          if (v && (v.min || v.max)) filters.push({ key: q.questionKey, answerType: 'NUMBER', operator: 'between', value: { min: v.min || undefined, max: v.max || undefined } });
          break;
        case 'RADIO':
          filters.push({ key: q.questionKey, answerType: 'RADIO', operator: 'equals', value: v });
          break;
        case 'MULTISELECT':
          if (Array.isArray(v) && v.length > 0) filters.push({ key: q.questionKey, answerType: 'MULTISELECT', operator: 'in', value: v });
          break;
        case 'TEXT':
        case 'TEXT_AREA':
          filters.push({ key: q.questionKey, answerType: 'TEXT', operator: 'contains', value: v });
          break;
        case 'LIST_OBJECT':
          // quick search across items
          filters.push({ key: q.questionKey, answerType: 'LIST_OBJECT', operator: 'contains', value: String(v) });
          break;
        default:
          break;
      }
    }
    onChange(filters);
  }, [answers, questions]);

  const renderControl = (q: QuestionDto) => {
    const key = q.questionKey;
    const val = answers[key];
    switch (q.answerType as AnswerType) {
      case 'BOOLEAN':
        return (
          <TextField
            select size="small" label={q.questionShorten || q.questionName}
            value={val ?? 'any'} onChange={(e) => setAnswers(a => ({ ...a, [key]: e.target.value }))}
          >
            <MenuItem value={'any'}>Any</MenuItem>
            <MenuItem value={'yes'}>Yes</MenuItem>
            <MenuItem value={'no'}>No</MenuItem>
          </TextField>
        );
      case 'DATE':
        return (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" type="date" label={(q.questionShorten || q.questionName) + ' from'} InputLabelProps={{ shrink: true }} value={val?.from || ''} onChange={(e) => setAnswers(a => ({ ...a, [key]: { ...(a[key] || {}), from: e.target.value } }))} />
            <TextField size="small" type="date" label={(q.questionShorten || q.questionName) + ' to'} InputLabelProps={{ shrink: true }} value={val?.to || ''} onChange={(e) => setAnswers(a => ({ ...a, [key]: { ...(a[key] || {}), to: e.target.value } }))} />
          </Stack>
        );
      case 'NUMBER':
        return (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" type="number" label={(q.questionShorten || q.questionName) + ' min'} value={val?.min || ''} onChange={(e) => setAnswers(a => ({ ...a, [key]: { ...(a[key] || {}), min: e.target.value } }))} />
            <TextField size="small" type="number" label={(q.questionShorten || q.questionName) + ' max'} value={val?.max || ''} onChange={(e) => setAnswers(a => ({ ...a, [key]: { ...(a[key] || {}), max: e.target.value } }))} />
          </Stack>
        );
      case 'RADIO':
        return (
          <Autocomplete
            size="small"
            options={(q.options || []).map(o => o.key)}
            value={val || ''}
            onChange={(_, newValue) => setAnswers(a => ({ ...a, [key]: newValue }))}
            renderInput={(params) => (<TextField {...params} label={q.questionShorten || q.questionName} placeholder="Select" />)}
          />
        );
      case 'MULTISELECT':
        return (
          <Autocomplete
            multiple size="small"
            options={(q.options || []).map(o => o.key)}
            value={Array.isArray(val) ? val : []}
            onChange={(_, newValue) => setAnswers(a => ({ ...a, [key]: newValue }))}
            renderInput={(params) => (<TextField {...params} label={q.questionShorten || q.questionName} placeholder="Select options" />)}
          />
        );
      case 'TEXT':
      case 'TEXT_AREA':
        return (
          <TextField size="small" label={q.questionShorten || q.questionName} value={val || ''} onChange={(e) => setAnswers(a => ({ ...a, [key]: e.target.value }))} />
        );
      case 'LIST_OBJECT':
        return (
          <TextField size="small" label={(q.questionShorten || q.questionName) + ' contains'} value={val || ''} onChange={(e) => setAnswers(a => ({ ...a, [key]: e.target.value }))} />
        );
      default:
        return null;
    }
  };

  if (!professionKeys || professionKeys.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Profession Questions</Typography>
      <Stack spacing={1}>
        {questions.map(q => (
          <Box key={q.questionKey}>
            {renderControl(q)}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default DynamicProfessionFilters;

